import { useMemo } from "react";
import moment from "moment";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";

/**
 * The planning horizon is the farthest future date the backend schedules up to.
 * It is configured on the backend (TIME_HORIZON_WEEKS) and surfaced to the app
 * via the `apiInfo` endpoint (`horizonWeeks`). We never hardcode it.
 *
 * A date is "past the horizon" when it falls strictly after the horizon day —
 * the horizon day itself is the last plannable day and is still in-bounds.
 */
export interface HorizonInfo {
  /** Configured horizon date at local midnight, or null until apiInfo loads. */
  horizonDate: Date | null;
  /** Horizon date as YYYY-MM-DD, or null until apiInfo loads. */
  horizonDateStr: string | null;
  /** True when `date` is strictly after the horizon day. */
  isPastHorizon: (date: Date | string | null | undefined) => boolean;
}

export const useHorizon = (): HorizonInfo => {
  const UserStore = useStore(UserContext);
  const weeks = UserStore.apiInfo?.horizonWeeks as number | undefined;

  return useMemo(() => {
    const horizonDate = weeks ? moment().startOf("day").add(weeks, "weeks").toDate() : null;
    const horizonDateStr = horizonDate ? moment(horizonDate).format("YYYY-MM-DD") : null;

    const isPastHorizon = (date: Date | string | null | undefined): boolean => {
      if (!horizonDate || !date) return false;
      return moment(date).startOf("day").isAfter(moment(horizonDate).startOf("day"));
    };

    return { horizonDate, horizonDateStr, isPastHorizon };
  }, [weeks]);
};

export default useHorizon;
